import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// BSC industrial palette (Bright/Light Theme)
const C = {
  bg:       '#F8F9FA', // Crisp, bright background
  panel:    '#FFFFFF', // Pure white login card
  panel2:   '#F1F3F5', // Soft gray for text inputs
  border:   '#DDE1E5', // Clean, defined borders
  text:     '#1A1A1A', // Dark charcoal for primary text
  muted:    '#5C6370', // Medium gray for labels and helper text
  faint:    '#868E96', // Light gray for disabled status
  amber:    '#D97706', // Deep industrial amber (darkened for light BG contrast)
  red:      '#DC2626', // High-visibility warning red
  mono: "'JetBrains Mono', ui-monospace, monospace",
  sans: "'DM Sans', system-ui, sans-serif",
};

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');
  const [expired, setExpired] = useState(false);

  // If a session just expired, explain it once
  useEffect(() => {
    if (sessionStorage.getItem('bsc_session_expired')) {
      setExpired(true);
      sessionStorage.removeItem('bsc_session_expired');
    }
  }, []);

  // Smart redirect helper based on role
  const handleRoleRedirection = (currentUser) => {
    if (!currentUser) return;
    if (currentUser.role === 'OPERATOR') {
      navigate('/forms', { replace: true });
    } else if (currentUser.role === 'PHOTO_REVIEWER') {
      navigate('/photos', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  // Already logged in? Divert immediately to proper workspace
  useEffect(() => {
    if (user) {
      handleRoleRedirection(user);
    }
  }, [user]);

  async function submit(e) {
    if (e?.preventDefault) e.preventDefault();
    if (!username || !password) { setErr('Enter username and password.'); return; }
    setBusy(true); setErr(''); setExpired(false);
    try {
      // login() returns the complete userData object from AuthContext
      const userData = await login(username.trim(), password);
      handleRoleRedirection(userData);
    } catch (e2) {
      setErr(e2.response?.data?.message || e2.response?.data?.error || 'Invalid username or password.');
    } finally {
      setBusy(false);
    }
  }

  const input = {
    width: '100%', boxSizing: 'border-box', background: C.panel2,
    border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
    padding: '14px 16px', fontSize: 16, fontFamily: C.sans, outline: 'none',
    marginBottom: 14,
  };

  return (
    <div style={{
      minHeight: '100vh', background: C.bg, color: C.text, fontFamily: C.sans,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <form onSubmit={submit} style={{
        width: '100%', maxWidth: 380, background: C.panel,
        border: `1px solid ${C.border}`, borderRadius: 16, padding: '34px 28px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)', // Subtle shadow for light mode depth
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: C.amber }} />
          <span style={{
            fontFamily: C.mono, fontSize: 12, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: C.muted,
          }}>BSC Gate</span>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '6px 0 22px' }}>Sign in</h1>

        {expired && (
          <div style={{
            background: '#FFFBEB', border: `1px solid ${C.amber}`, borderRadius: 10,
            padding: '10px 14px', color: '#B45309', fontSize: 13, marginBottom: 16,
          }}>Your session expired. Please sign in again.</div>
        )}
        {err && (
          <div style={{
            background: '#FDF2F2', border: `1px solid ${C.red}`, borderRadius: 10,
            padding: '10px 14px', color: '#9C1A1A', fontSize: 13, marginBottom: 16,
          }}>`{err}`</div>
        )}

        <input style={input} placeholder="Username" autoFocus
          value={username} onChange={e => setUsername(e.target.value)} />
        <input style={input} placeholder="Password" type="password"
          value={password} onChange={e => setPassword(e.target.value)} />

        <button type="submit" disabled={busy} style={{
          width: '100%', padding: '15px 0', borderRadius: 12,
          background: busy ? C.panel2 : C.amber, 
          color: busy ? C.faint : '#FFFFFF', // High-contrast crisp white text over amber
          border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
          fontFamily: C.mono, fontSize: 14, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4,
          transition: 'background .15s',
        }}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}